import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { scaleOrdinal } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Grid } from '@visx/grid';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';

import {
  usePanelDateDomain,
  useSelectedDateDomain,
  useActiveTimelineData,
  useOnSelectedDateDomainChange,
  useStyling,
  useUserEventTypes,
  useSelectedEventType,
} from './EventTimelineState';
import { UserEventTooltip } from './EventTooltips';
import services from '../../../services';
import { getTimezone } from '../../../bundles/application';
import {
  checkIsOnline,
  displayNotification,
  getNotification,
} from '../../../bundles/notifications';
import { useClientSize } from '../../../utils';
import { getDateTickFormatter } from '../../../datetimeUtils';
import {
  USER_EVENTS,
  EVENT_DEFAULT_MARGIN,
  EVENT_TYPE_USER,
  STATELESS_EVENTS,
} from '../../../constants';
import EventLegends from './EventLegends';
import {
  formatYTick,
  useEventPicker,
  useTimelineDragZoom,
  useTimeScale,
  useTimelineTooltipPortal,
  getTimelineConstants,
  defaultTooltipStyles,
} from './utils';
import { getLimitedUsers } from '../../../bundles/ad';

function renderUserEventTooltip(event, timezone, comments = [], showStatus = false) {
  return {
    eventId: event.id,
    tooltip: (
      <UserEventTooltip
        event={event}
        comments={comments}
        showStatus={showStatus}
        timezone={timezone}
      />
    ),
  };
}

const eventStatus = (properties) => {
  const { state } = properties.event;
  const eventState = USER_EVENTS.find((event) => event.id === state);
  return eventState?.border;
};

function getTaskSortedEventTypes(userEventSubTypes, allEventSubTypes) {
  const taskTypes = userEventSubTypes
    .filter((eventSubType) => allEventSubTypes.find(({ id }) => eventSubType === id)?.task)
    .toSorted((a, b) => a - b);
  const nonTaskTypes = userEventSubTypes
    .filter((eventSubType) => !taskTypes.includes(eventSubType))
    .toSorted((a, b) => a - b);
  return [...taskTypes, ...nonTaskTypes];
}

const UserTimeline = ({ margin = EVENT_DEFAULT_MARGIN, plotSize = 4, fullWidth, onOpenEvent }) => {
  const timezone = useSelector(getTimezone);
  const users = useSelector(getLimitedUsers);
  const dispatch = useDispatch();

  const [{ width }, clientReference] = useClientSize();

  const { tickStroke, background, indicatorStroke, xTickLabelProps, yTickLabelProps } =
    useStyling();

  const selectedDateDomain = useSelectedDateDomain();
  const panelDateDomain = usePanelDateDomain();
  const [selectedEventType] = useSelectedEventType();
  const onZoomChange = useOnSelectedDateDomainChange();
  let periodInMs =
    selectedDateDomain?.length === 2 ? selectedDateDomain[1] - selectedDateDomain[0] : 0;
  if (!periodInMs) {
    periodInMs = panelDateDomain?.length === 2 ? panelDateDomain[1] - panelDateDomain[0] : 0;
  }
  const hitMaxZoom = periodInMs <= 2;

  const {
    response: {
      userEvents = [],
      userEventTypes = [],
      dateDomain = selectedDateDomain?.length ? selectedDateDomain : panelDateDomain,
    } = {},
  } = useActiveTimelineData();
  const allEventTypes = useUserEventTypes();

  const userEventsReference = useRef(userEvents);
  userEventsReference.current = userEvents;

  const [activeUserEvent, setActiveUserEvent] = useState(null);
  const [eventComments, setEventComments] = useState(null);
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    updateTooltip,
    hideTooltip,
  } = useTooltip();
  const { containerRef, TooltipInPortal } = useTimelineTooltipPortal();
  const onOpenUserEvent = useCallback(
    (event) => {
      const { index } = event.target.dataset;
      if (onOpenEvent && index) {
        const userEvent = userEventsReference.current[index];
        onOpenEvent({
          event: userEvent,
          id: userEvent.id,
          type: EVENT_TYPE_USER,
        });
      }
    },
    [onOpenEvent]
  );
  const openTooltip = useCallback(
    async (event) => {
      const coords = localPoint(event.target.ownerSVGElement, event);
      const { index } = event.target.dataset;
      const userEvent = userEventsReference.current[index];
      if (userEvent) {
        setActiveUserEvent(() => userEvent);
        userEvent.assigneeName =
          users.find((user) => user.id === userEvent.assignedTo)?.name || 'Not Assigned';
      }
      showTooltip({
        tooltipLeft: coords.x,
        tooltipTop: coords.y,
        tooltipData: renderUserEventTooltip(userEvent, timezone),
      });
    },
    [users, showTooltip, timezone]
  );

  useEffect(() => {
    if (activeUserEvent) {
      (async () => {
        const { values: comments = [] } = await services.collaboration
          .getComments({ parentId: activeUserEvent.id })
          .then(
            (response) => response,
            (error) => {
              console.error('Unable to fetch comments:', error);
              dispatch(checkIsOnline());
              dispatch(displayNotification(getNotification('getComments', 'error')()));
              return {};
            }
          );
        setEventComments(() => ({ eventId: activeUserEvent.id, comments }));
      })();
    }
  }, [activeUserEvent, dispatch]);

  useEffect(() => {
    if (
      eventComments?.comments?.length &&
      activeUserEvent &&
      eventComments.eventId === activeUserEvent.id &&
      eventComments.eventId === tooltipData?.eventId
    ) {
      updateTooltip({
        tooltipOpen,
        tooltipLeft,
        tooltipTop,
        tooltipData: renderUserEventTooltip(activeUserEvent, timezone, eventComments.comments),
      });
    }
  }, [
    eventComments,
    activeUserEvent,
    tooltipData?.eventId,
    updateTooltip,
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    timezone,
  ]);

  const closeUserEventTooltip = useCallback(() => {
    setActiveUserEvent(() => null);
    setEventComments(() => null);
    hideTooltip();
  }, [hideTooltip]);

  const { USER_EVENT_HEIGHT, IOT_EVENT_HEIGHT } = getTimelineConstants();
  let height = USER_EVENT_HEIGHT * userEventTypes.length + margin.top + margin.bottom;

  if (userEventTypes.length === 0) {
    height += 100;
  }

  const xSize = width - margin.right - margin.left;
  const ySize = height - margin.top - margin.bottom;
  const xScale = useTimeScale(dateDomain, width, margin);
  const sortedEventTypes = getTaskSortedEventTypes(userEventTypes, allEventTypes);
  const yScale = useMemo(() => {
    const domain = sortedEventTypes.length > 0 ? [...sortedEventTypes] : ['default'];
    let range = sortedEventTypes.map(
      (_, index) => ySize + margin.top - USER_EVENT_HEIGHT * (index + 1)
    );
    if (sortedEventTypes.length === 0) {
      range = [margin.top + IOT_EVENT_HEIGHT / 2];
    }
    return scaleOrdinal({ domain, range });
  }, [sortedEventTypes, ySize, margin.top, USER_EVENT_HEIGHT, IOT_EVENT_HEIGHT]);

  const { zoomReference, onDragStart, onDrag, onDragEnd } = useTimelineDragZoom(
    xScale,
    hitMaxZoom,
    onZoomChange
  );

  const [eventPicker] = useEventPicker();

  const userShapes = useMemo(
    () => (
      <>
        {userEvents.map((event, index) => {
          const size = plotSize * 4;
          const halfSize = size / 2;
          const svg = USER_EVENTS.find((x) => x.id === event.state);
          const isStateLessEvent = STATELESS_EVENTS.includes(event.type);

          if (event.from === event.to || !event.to) {
            const fillColor = isStateLessEvent ? '#FFFFFF' : svg?.color || '#A9A2AB';
            return (
              <rect
                className="timeline-chart-event"
                key={event.id}
                x={xScale(new Date(event.from).getTime()) - halfSize}
                y={yScale(event.type) - halfSize}
                rx={halfSize}
                ry={halfSize}
                width={size}
                height={size}
                fill={fillColor}
                stroke={svg?.border || '#666666'}
                strokeWidth="2"
                onMouseOver={openTooltip}
                onMouseOut={closeUserEventTooltip}
                onClick={onOpenUserEvent}
                data-index={index}
              />
            );
          }
          const startDate = new Date(event.from).getTime();
          const endDate = new Date(event.to).getTime();
          const startDomain = dateDomain[0];
          const endDomain = dateDomain[1];
          const croppedStart = startDate <= startDomain;
          const croppedEnd = endDate >= endDomain;

          const startPlot = croppedStart ? xScale(startDomain) : xScale(startDate);
          const endPlot = croppedEnd ? xScale(endDomain) : xScale(endDate);
          return (
            <rect
              className="timeline-chart-event"
              key={event.id}
              x={startPlot - halfSize}
              y={yScale(event.type) - halfSize}
              rx={halfSize}
              ry={halfSize}
              width={size + Math.max(0, endPlot - startPlot)}
              height={size}
              fill={svg?.color || '#A9A2AB'}
              stroke={svg?.border || '#666666'}
              strokeWidth="2"
              onMouseOver={openTooltip}
              onMouseOut={closeUserEventTooltip}
              onClick={onOpenUserEvent}
              data-index={index}
            />
          );
        })}
      </>
    ),
    [
      userEvents,
      plotSize,
      dateDomain,
      xScale,
      yScale,
      openTooltip,
      closeUserEventTooltip,
      onOpenUserEvent,
    ]
  );

  const presentIndicator = useMemo(() => {
    const now = Date.now();

    if (dateDomain[1] - now <= 0) {
      return null;
    }

    const x = xScale(now) || 0;
    return (
      <line
        x1={x}
        y1={0}
        x2={x}
        y2={height - margin.bottom}
        stroke={indicatorStroke}
        strokeOpacity={0.5}
      />
    );
  }, [dateDomain, xScale, height, margin.bottom, indicatorStroke]);

  return (
    <>
      {selectedEventType === EVENT_TYPE_USER && (
        <>
          <EventLegends
            fullWidth={fullWidth}
            eventType={EVENT_TYPE_USER}
          />
          <div
            ref={clientReference}
            className="event-timeline-chart-area"
          >
            <svg
              width={width}
              height={height}
              ref={containerRef}
              onTouchMove={onDrag}
              onTouchEnd={onDragEnd}
              onMouseUp={onDragEnd}
              onMouseLeave={onDragEnd}
              onMouseMove={onDrag}
            >
              {presentIndicator}
              <rect
                ref={zoomReference}
                fillOpacity={0}
                stroke="currentColor"
                strokeDasharray="1,2"
                width={0}
                height={height - margin.top - margin.bottom - 10}
                y={margin.top - 5}
              />
              <rect
                fillOpacity={0}
                x={margin.left}
                stroke={tickStroke}
                width={Math.max(0, width - margin.left - margin.right - 2)}
                height={height - margin.bottom}
                onTouchStart={onDragStart}
                onMouseDown={onDragStart}
              />
              <Grid
                xScale={xScale}
                yScale={yScale}
                width={Math.max(0, width - margin.right - margin.left)}
                height={height}
                left={margin.left}
                columnLineStyle={{ display: 'none' }}
                stroke={tickStroke}
              />
              {userShapes}
              <rect
                // covers any overflow from chart
                x={0}
                y={0}
                width={margin.left}
                height={height}
                fill={background}
              />
              <AxisLeft
                scale={yScale}
                left={margin.left}
                hideAxisLine
                stroke={tickStroke}
                tickStroke="transparent"
                tickLabelProps={yTickLabelProps}
                tickFormat={(value) => formatYTick(value, allEventTypes)}
              />
              <AxisBottom
                scale={xScale}
                top={height - margin.bottom}
                hideAxisLine
                stroke={tickStroke}
                tickStroke={tickStroke}
                tickLabelProps={xTickLabelProps}
                numTicks={Math.max(Math.floor(xSize / 80), 1)}
                tickFormat={getDateTickFormatter(periodInMs, timezone, {
                  scale: xScale,
                  width: width - margin.right,
                })}
              />
              <rect
                // covers removed end tick line
                x={width - margin.right - 20}
                y={height - margin.bottom + 1}
                width={30}
                height={8}
                fill={background}
              />
            </svg>
            {tooltipOpen && (
              <TooltipInPortal
                key={`${tooltipData?.eventId}-${tooltipLeft}-${tooltipTop}`}
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultTooltipStyles,
                  padding: 0,
                  borderRadius: '0.1rem',
                  backgroundColor: 'transparent',
                  border: `1px solid ${eventStatus(tooltipData?.tooltip.props)}`,
                  zIndex: 10_000,
                }}
              >
                {tooltipData.tooltip}
              </TooltipInPortal>
            )}
            {eventPicker}
          </div>
        </>
      )}
    </>
  );
};

export default UserTimeline;
